import { useState, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Type,
  MousePointerClick,
  Image,
  Minus,
  ArrowUpDown,
  Square,
  Columns,
  PanelTop,
  PanelBottom,
  GripVertical,
  Trash2,
  ChevronUp,
  ChevronDown,
  Info,
  Plus,
  Palette,
} from "lucide-react";

// ─── Block Type Definitions ──────────────────────────────────
const BLOCK_TYPES = [
  { type: "header", label: "Header", icon: PanelTop, description: "Brand header with title" },
  { type: "text", label: "Text", icon: Type, description: "Text paragraph" },
  { type: "button", label: "Button", icon: MousePointerClick, description: "Call-to-action button" },
  { type: "image", label: "Image", icon: Image, description: "Image from URL" },
  { type: "info_box", label: "Info Box", icon: Info, description: "Highlighted callout" },
  { type: "two_columns", label: "Columns", icon: Columns, description: "Two-column layout" },
  { type: "divider", label: "Divider", icon: Minus, description: "Horizontal line" },
  { type: "spacer", label: "Spacer", icon: ArrowUpDown, description: "Vertical space" },
  { type: "footer", label: "Footer", icon: PanelBottom, description: "Email footer" },
];

const DEFAULT_PROPS = {
  header: { title: "RNB Consulting Group", subtitle: "", bgColor: "#1A2636" },
  text: { content: "", align: "left", fontSize: "16", color: "#333333", bold: false },
  button: { text: "Click Here", url: "", bgColor: "#1FC6B7", textColor: "#ffffff", align: "center", borderRadius: "6" },
  image: { src: "", alt: "", width: "100", align: "center" },
  divider: { color: "#E8E8E8" },
  spacer: { height: "20" },
  info_box: { content: "", bgColor: "#EAF6FB", borderColor: "#2471A3" },
  two_columns: { leftContent: "", rightContent: "" },
  footer: { text: "RNB Consulting Group | www.rnbconsultinggroup.com" },
};

// ─── Color Presets ───────────────────────────────────────────
const COLOR_PRESETS = [
  "#1A2636", "#2471A3", "#1FC6B7", "#ffffff", "#333333",
  "#E8E8E8", "#EAF6FB", "#FFF3CD", "#F8D7DA", "#D4EDDA",
];

// ─── Inline Color Picker ─────────────────────────────────────
const ColorPicker = ({ value, onChange, label }) => (
  <div>
    {label && <p className="text-[11px] text-gray-400 mb-1">{label}</p>}
    <div className="flex items-center gap-1.5">
      {COLOR_PRESETS.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          className={`w-5 h-5 rounded-full border-2 transition-transform ${
            value === c ? "border-rnb-blue scale-110" : "border-gray-200"
          }`}
          style={{ backgroundColor: c }}
        />
      ))}
      <input
        type="color"
        value={value || "#333333"}
        onChange={(e) => onChange(e.target.value)}
        className="w-5 h-5 rounded cursor-pointer border-0 p-0"
      />
    </div>
  </div>
);

// ─── Block Settings Panels ───────────────────────────────────
const BlockSettings = ({ block, onUpdate }) => {
  const p = block.props;
  const set = (key, val) => onUpdate({ ...block, props: { ...p, [key]: val } });

  switch (block.type) {
    case "header":
      return (
        <div className="space-y-2">
          <Input value={p.title} onChange={(e) => set("title", e.target.value)} placeholder="Header title" className="text-sm h-8" />
          <Input value={p.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtitle (optional)" className="text-sm h-8" />
          <ColorPicker label="Background" value={p.bgColor} onChange={(v) => set("bgColor", v)} />
        </div>
      );
    case "text":
      return (
        <div className="space-y-2">
          <Textarea value={p.content} onChange={(e) => set("content", e.target.value)} placeholder="Enter text... Use {variable_name} for dynamic content" rows={3} className="text-sm" />
          <div className="flex items-center gap-3">
            <select value={p.align} onChange={(e) => set("align", e.target.value)} className="border rounded-lg px-2 py-1 text-xs">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
            <select value={p.fontSize} onChange={(e) => set("fontSize", e.target.value)} className="border rounded-lg px-2 py-1 text-xs">
              <option value="14">Small</option>
              <option value="16">Medium</option>
              <option value="18">Large</option>
              <option value="22">XL</option>
            </select>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={p.bold || false} onChange={(e) => set("bold", e.target.checked)} className="rounded" />
              Bold
            </label>
          </div>
          <ColorPicker label="Text Color" value={p.color} onChange={(v) => set("color", v)} />
        </div>
      );
    case "button":
      return (
        <div className="space-y-2">
          <Input value={p.text} onChange={(e) => set("text", e.target.value)} placeholder="Button text" className="text-sm h-8" />
          <Input value={p.url} onChange={(e) => set("url", e.target.value)} placeholder="Link URL or {payment_link}" className="text-sm h-8" />
          <div className="flex items-center gap-3">
            <select value={p.align} onChange={(e) => set("align", e.target.value)} className="border rounded-lg px-2 py-1 text-xs">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
            <select value={p.borderRadius} onChange={(e) => set("borderRadius", e.target.value)} className="border rounded-lg px-2 py-1 text-xs">
              <option value="0">Sharp</option>
              <option value="6">Rounded</option>
              <option value="24">Pill</option>
            </select>
          </div>
          <ColorPicker label="Button Color" value={p.bgColor} onChange={(v) => set("bgColor", v)} />
          <ColorPicker label="Text Color" value={p.textColor} onChange={(v) => set("textColor", v)} />
        </div>
      );
    case "image":
      return (
        <div className="space-y-2">
          <Input value={p.src} onChange={(e) => set("src", e.target.value)} placeholder="Image URL" className="text-sm h-8" />
          <Input value={p.alt} onChange={(e) => set("alt", e.target.value)} placeholder="Alt text" className="text-sm h-8" />
          <div className="flex items-center gap-3">
            <div>
              <p className="text-[11px] text-gray-400 mb-1">Width %</p>
              <input type="range" min="30" max="100" value={p.width} onChange={(e) => set("width", e.target.value)} className="w-24" />
              <span className="text-xs text-gray-500 ml-2">{p.width}%</span>
            </div>
            <select value={p.align} onChange={(e) => set("align", e.target.value)} className="border rounded-lg px-2 py-1 text-xs">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
      );
    case "info_box":
      return (
        <div className="space-y-2">
          <Textarea value={p.content} onChange={(e) => set("content", e.target.value)} placeholder="Callout text..." rows={3} className="text-sm" />
          <ColorPicker label="Background" value={p.bgColor} onChange={(v) => set("bgColor", v)} />
          <ColorPicker label="Border" value={p.borderColor} onChange={(v) => set("borderColor", v)} />
        </div>
      );
    case "two_columns":
      return (
        <div className="space-y-2">
          <Textarea value={p.leftContent} onChange={(e) => set("leftContent", e.target.value)} placeholder="Left column..." rows={2} className="text-sm" />
          <Textarea value={p.rightContent} onChange={(e) => set("rightContent", e.target.value)} placeholder="Right column..." rows={2} className="text-sm" />
        </div>
      );
    case "divider":
      return <ColorPicker label="Color" value={p.color} onChange={(v) => set("color", v)} />;
    case "spacer":
      return (
        <div>
          <p className="text-[11px] text-gray-400 mb-1">Height</p>
          <input type="range" min="5" max="60" value={p.height} onChange={(e) => set("height", e.target.value)} className="w-full" />
          <span className="text-xs text-gray-500">{p.height}px</span>
        </div>
      );
    case "footer":
      return (
        <Input value={p.text} onChange={(e) => set("text", e.target.value)} placeholder="Footer text" className="text-sm h-8" />
      );
    default:
      return null;
  }
};

// ─── Single Block Row ────────────────────────────────────────
const BlockRow = ({ block, index, total, onUpdate, onDelete, onMove, isActive, onSelect }) => {
  const typeDef = BLOCK_TYPES.find((b) => b.type === block.type);
  const Icon = typeDef?.icon || Square;

  return (
    <div className={`border rounded-xl transition-all duration-200 ${isActive ? "border-rnb-blue bg-rnb-blue/5 shadow-sm" : "border-gray-200 bg-white hover:border-gray-300"}`}>
      {/* Block header */}
      <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" onClick={onSelect} data-testid={`block-${index}-header`}>
        <GripVertical className="w-3.5 h-3.5 text-gray-300" />
        <Icon className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-xs font-medium text-rnb-navy flex-1">{typeDef?.label || block.type}</span>
        <div className="flex items-center gap-0.5">
          <button onClick={(e) => { e.stopPropagation(); onMove(-1); }} disabled={index === 0} className="p-0.5 rounded text-gray-300 hover:text-rnb-navy disabled:opacity-30">
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onMove(1); }} disabled={index === total - 1} className="p-0.5 rounded text-gray-300 hover:text-rnb-navy disabled:opacity-30">
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-0.5 rounded text-gray-300 hover:text-red-500 ml-1" data-testid={`block-${index}-delete`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Block settings (expanded) */}
      {isActive && (
        <div className="px-3 pb-3 border-t border-gray-100 pt-2">
          <BlockSettings block={block} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
};

// ─── Main Editor Component ───────────────────────────────────
const EmailBlockEditor = ({ blocks, onChange, variableRegistry, onInsertVariable }) => {
  const [activeBlock, setActiveBlock] = useState(null);

  const addBlock = useCallback((type) => {
    const newBlock = { id: `block_${Date.now()}`, type, props: { ...DEFAULT_PROPS[type] } };
    onChange([...blocks, newBlock]);
    setActiveBlock(blocks.length);
  }, [blocks, onChange]);

  const updateBlock = useCallback((index, updatedBlock) => {
    const next = [...blocks];
    next[index] = updatedBlock;
    onChange(next);
  }, [blocks, onChange]);

  const deleteBlock = useCallback((index) => {
    onChange(blocks.filter((_, i) => i !== index));
    setActiveBlock(null);
  }, [blocks, onChange]);

  const moveBlock = useCallback((index, dir) => {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[newIdx]] = [next[newIdx], next[index]];
    onChange(next);
    setActiveBlock(newIdx);
  }, [blocks, onChange]);

  return (
    <div className="flex flex-col gap-4">
      {/* Block Palette — add new blocks */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">Add Content Block</p>
        <div className="flex flex-wrap gap-1.5" data-testid="block-palette">
          {BLOCK_TYPES.map((bt) => {
            const Icon = bt.icon;
            return (
              <button
                key={bt.type}
                onClick={() => addBlock(bt.type)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:border-rnb-blue hover:text-rnb-blue hover:bg-rnb-blue/5 transition-colors"
                title={bt.description}
                data-testid={`add-block-${bt.type}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {bt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Variable Palette */}
      {variableRegistry && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">Insert Variable</p>
          <div className="flex flex-wrap gap-1" data-testid="variable-palette">
            {Object.entries(variableRegistry).map(([cat, info]) =>
              info.variables.map((v) => (
                <button
                  key={v.key}
                  onClick={() => onInsertVariable?.(v.key)}
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-gray-500 hover:bg-rnb-blue/10 hover:text-rnb-blue transition-colors"
                  title={`${v.label} — e.g. ${v.sample}`}
                >
                  {`{${v.key}}`}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Block Stack */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">Email Blocks ({blocks.length})</p>
        {blocks.length === 0 ? (
          <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center">
            <Plus className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Click a block type above to start building</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="block-stack">
            {blocks.map((block, index) => (
              <BlockRow
                key={block.id || index}
                block={block}
                index={index}
                total={blocks.length}
                onUpdate={(b) => updateBlock(index, b)}
                onDelete={() => deleteBlock(index)}
                onMove={(dir) => moveBlock(index, dir)}
                isActive={activeBlock === index}
                onSelect={() => setActiveBlock(activeBlock === index ? null : index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailBlockEditor;
